import { useCallback } from 'react';
import { HERO_CONFIG } from '../config/constants';

const { VIDEOS, VIDEO_DURATIONS } = HERO_CONFIG;

/**
 * 動画管理のカスタムフック
 * @param {Object} videoRefs - 動画要素のref
 * @param {Function} updateHeroState - ヒーロー状態更新関数
 * @returns {Object} 動画管理関数
 */
export const useVideoManager = (videoRefs, updateHeroState) => {
  const createHandleLoadedMetadata = useCallback(
    index => {
      return () => {
        const video = videoRefs.current[index].current;
        if (video) {
          const actualDuration = video.duration * 1000;
          const newDurations = [...Array(VIDEOS.length).fill(0)];
          newDurations[index] = actualDuration;
          updateHeroState({ videoDurations: newDurations });

          if (process.env.NODE_ENV === 'development') {
            console.log(
              `Video ${index + 1} actual duration: ${actualDuration}ms (${video.duration}s), config: ${VIDEO_DURATIONS[index]}s`
            );
          }
        }
      };
    },
    [videoRefs, updateHeroState]
  );

  const playVideo = useCallback(
    index => {
      const video = videoRefs.current[index].current;
      if (video && process.env.NODE_ENV !== 'test') {
        if (video.paused) {
          video.currentTime = 0;
          // 再生前に少し遅延を入れて競合状態を回避
          setTimeout(() => {
            if (video && !video.paused) return; // 既に再生中ならスキップ
            video.play().catch(error => {
              // AbortErrorは無視（正常な停止による中断）
              if (
                error.name !== 'AbortError' &&
                process.env.NODE_ENV === 'development'
              ) {
                console.error('Video playback failed:', {
                  error,
                  videoIndex: index,
                  videoSrc: video.src,
                  currentTime: video.currentTime,
                  readyState: video.readyState,
                });
              }
            });
          }, 50);

          if (process.env.NODE_ENV === 'development') {
            console.log(`Started playing video ${index + 1}`);
          }
        }
      }
    },
    [videoRefs]
  );

  const pauseVideo = useCallback(
    index => {
      const video = videoRefs.current[index].current;
      if (video && process.env.NODE_ENV !== 'test' && !video.paused) {
        video.pause();

        if (process.env.NODE_ENV === 'development') {
          console.log(`Paused video ${index + 1}`);
        }
      }
    },
    [videoRefs]
  );

  return {
    createHandleLoadedMetadata,
    playVideo,
    pauseVideo,
  };
};
